/**
 * shareService.ts
 * MIGRATION NOTE: Share link generation with production domain and human-readable URLs
 * Date: 2025-12-12
 */

// Production domain for all share links
const PRODUCTION_DOMAIN = 'https://platform.mystflo.com';

export interface ShareLinkData {
  token: string;
  url: string;
}

export interface ShareTokenData {
  assistantId: string;
  createdAt: number;
}

/**
 * Convert text to URL-friendly slug
 * "Pre-Sale Waitlist Campaign" → "pre-sale-waitlist-campaign"
 */
function toSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')     // Replace spaces with hyphens
    .replace(/-+/g, '-')      // Replace multiple hyphens with single
    .replace(/^-|-$/g, '');   // Remove leading/trailing hyphens
}

/**
 * Generate a share link for an assistant with human-readable URL
 * Format: https://platform.mystflo.com/assistants/pre-sale-waitlist-campaign/TMPL-7K2M
 */
export async function createShareLink(
  assistantId: string, 
  assistantName?: string
): Promise<ShareLinkData> {
  // Build URL with optional slug for human-readability
  let url: string;
  if (assistantName) {
    const slug = toSlug(assistantName);
    url = `${PRODUCTION_DOMAIN}/assistants/${slug}/${assistantId}`;
  } else {
    url = `${PRODUCTION_DOMAIN}/assistants/${assistantId}`;
  }

  return { 
    token: assistantId, // Keep for backwards compatibility
    url 
  };
}

/**
 * Resolve a share token/templateId to assistant data
 * Now uses direct templateId instead of generated tokens
 */
export async function resolveShareToken(templateId: string): Promise<ShareTokenData | null> {
  // With direct templateId approach, we just return the ID
  // The actual assistant lookup happens in the component
  return {
    assistantId: templateId,
    createdAt: Date.now(),
  };
}